import React from 'react';
import { Box, Typography } from '@mui/material';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import TextInput from 'src/components/UI/input/TextInput';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { stripMemberReferencesToPid } from '../../pedigree/edgeIntegrity';

function DeleteNode({ node, user, setMemberRecords, handleClose }) {
  const [inputValue, setInputValue] = React.useState('');
  const [error, setError] = React.useState(false);
  const { setSnack } = React.useContext(SnackContext);

  const onChange = (e) => {
    setInputValue(e.target.value);
    setError(false);
  };

  const isStructuralProband = !node.familyMemberId && !!node.proBandId;

  const proceedToDelete = async () => {
    const displayedUpn = String(node.upn || node.pid);
    if (String(inputValue).trim() !== displayedUpn) {
      setError(true);
      return;
    }

    const pid = node.pid;

    try {
      if (isStructuralProband) {
        await axios.post(
          APICONSTANTS.deleteStructuralProband.replace(':user_id', user._id),
          { pid }
        );
        window.location.reload();
        return;
      }

      await axios.post(
        APICONSTANTS.deletePedigreeFamilyMember
          .replace(':user_id', user._id)
          .replace(':member_id', node.familyMemberId)
      );

      // Functional update: state may have moved on while the POST was in
      // flight (save-echo identity backfill, concurrent edits) — derive the
      // deletion from `prev`, never from the render-time snapshot.
      setMemberRecords((prev) =>
        prev
          .filter((p) => p.pid !== pid)
          .map((p) => stripMemberReferencesToPid(p, pid))
      );
      handleClose();
    } catch {
      setSnack({
        status: true,
        msg: 'Failed to delete family member',
        severity: 'error'
      });
    }
  };

  return (
    <CustomDialog
      open={true}
      handleClose={handleClose}
      showActions={true}
      saveBtnLabel={'Confirm Delete'}
      title={'Delete Confirmation'}
      titleAppearance={'warning'}
      minHeight={200}
      onSave={proceedToDelete}
      allowBackgroundClose={handleClose}
    >
      <Box>
        <Typography
          sx={{
            fontWeight: 'bold'
          }}
        >
          {`Warning: This action will permanently delete the following family member's details, including all associated records and data.`}
        </Typography>
        <Box
          sx={{
            py: 2
          }}
        >
          <Typography>UPN : {node.upn || node.pid}</Typography>
          <Typography>
            Full Name: {node.firstName} {node.lastName}
          </Typography>
          <Typography>
            Family Member Id : {node.familyMemberId || node.proBandId}
          </Typography>
          <Typography>Date of Birth: {node.dob}</Typography>
        </Box>
        <Typography variant="subtitle2">
          Please note that this action cannot be undone, and all historical
          information linked to this family member will be permanently removed
          from the system.
        </Typography>
        {isStructuralProband && (
          <Typography
            variant="subtitle2"
            sx={{
              color: 'warning.main',
              pt: 1
            }}
          >
            This is the original proband. Deleting them will promote another
            family member to be the new family root. The page will reload after
            deletion.
          </Typography>
        )}
        <Box
          className="user-form"
          sx={{
            pt: 2
          }}
        >
          <TextInput
            label="Enter Family Member UPN to confirm deletion"
            error={error}
            helperText={error && 'Invalid Family Member UPN'}
            placeholder={String(node.upn || node.pid)}
            onChange={onChange}
          />
        </Box>
      </Box>
    </CustomDialog>
  );
}

export default DeleteNode;
